import { toPlainObject } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAsyncFn, useEffectOnce } from 'react-use';
import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';

import { fetchQuickPicks } from 'lib/AutolistAPI';
import { AdInfo } from 'lib/CarGurusAds';
import { Features, SourcePages } from 'lib/EventSchema';
import useAdSetup from 'lib/hooks/useAdSetup';
import { useAlert } from 'lib/hooks/useAlert';
import { useAutolistUser } from 'lib/hooks/useAutolistUser';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import useEvents from 'lib/hooks/useEvents';
import useFavorite from 'lib/hooks/useFavorite';
import useFetchFavorites from 'lib/hooks/useFetchFavorites';
import useFetchLeads from 'lib/hooks/useFetchLeads';
import { useLeadSubmitter } from 'lib/hooks/useLead';
import useSimilarListings from 'lib/hooks/useSimilarListingsViewModels';
import VehicleViewModel from 'lib/Presenters/VdpPresenter/Vehicle';
import { trackScreenView } from 'lib/Tracking';
import VdpPageViewEvent from 'lib/Tracking/Vdp/PageViewEvent';
import closeEvent from 'lib/Tracking/Vdp/Quickpicks/CloseEvent';
import VdpQuickPicksViewEvent from 'lib/Tracking/Vdp/QuickPicksViewEvent';
import { trackLeadSuccess } from 'lib/Tracking/LeadForm/FormSuccess';

import { firstVisitTrackingReceived } from 'redux-implementations/firstVisitTracking';
import { utmTrackingReceived } from 'redux-implementations/utmTracking';

import TrackingDataContext from 'contexts/TrackingDataContext';
import VdpEventContext from 'contexts/VdpEventContext';
import QuickPicksEventContext from 'contexts/QuickPicksEventContext';

import { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import {
  EnhancedLeadTrackingPageSource,
  EnhancedLeadTrackingPlatform,
} from 'types/Apis/EnhancedLeadTrackingFields';
import SearchResponse from 'types/Apis/SearchResponse';
import ApiResponse from 'types/Apis/VehicleApiResponse';
import { ExperienceApiResponse } from 'types/Apis/VehicleWithExperience';
import { LeadFormFields } from 'types/LeadForm';

import Footer from 'components/Footer';
import Header from 'components/Header';
import QuickPicksModal from 'components/QuickPicks/Modal';
import Vdp from 'components/Vdp';
import Geocode from 'types/Geocode';

import { omitNull } from 'lib/utils';
import { useAtom, useAtomValue } from 'jotai';
import requestedPriceAtom from 'atoms/RequestedPriceAtom';
import { pagePositionAtom } from 'lib/atoms/pagePosition';
import leadInfoAtom from 'atoms/LeadInfoAtom';
import { VdpScrollContainer } from './vdp-scroll-container';

export interface VdpPageProps {
  vehicle?: ApiResponse | null;
  experience?: ExperienceApiResponse | null;
  firstVisitTracking: {
    campaign?: string | null;
    referrer?: string | null;
    page: string | null;
  };
  utmTracking: {
    content?: string | null;
    campaign?: string | null;
    medium?: string | null;
    term?: string | null;
    source?: string | null;
  };
  isPaid: boolean;
  adInfo?: AdInfo | null;
}

export interface PresentVdpPageProps extends VdpPageProps {
  vehicle: ApiResponse;
  experience: ExperienceApiResponse;
  geolocation?: Geocode;
}

function VdpPage({
  vehicle,
  firstVisitTracking,
  utmTracking,
  experience,
  adInfo,
  geolocation,
}: PresentVdpPageProps): JSX.Element {
  const dispatch = useDispatch();
  const logger = useEvents();
  const { isMobile, isTablet } = useDeviceScreen();
  const router = useRouter();
  const pagePosition = useAtomValue(pagePositionAtom);
  dispatch(utmTrackingReceived(omitNull(utmTracking)));
  dispatch(firstVisitTrackingReceived(omitNull(firstVisitTracking)));

  const [requestedPriceValues, setRequestedPriceValues] = useAtom(requestedPriceAtom);
  const { adExtId, adPartnerId } = useAtomValue(leadInfoAtom);
  const requestedPrice = requestedPriceValues[vehicle.vin];
  useEffectOnce(() => {
    setRequestedPriceValues({ ...requestedPriceValues, selectedVin: vehicle.vin });
  });

  const viewModel = new VehicleViewModel(vehicle, experience);
  const { isFavorited, unfavorite, favorite } = useFavorite(viewModel.vin);
  const { showError } = useAlert();

  let carGurusAdInfo: AdInfo = {};
  if (adInfo) {
    carGurusAdInfo = { ...adInfo };
  }

  useAdSetup(carGurusAdInfo.ad_unit as string, {
    adUnit: carGurusAdInfo.ad_unit as string,
    make: carGurusAdInfo.make,
    model: carGurusAdInfo.model,
    year: carGurusAdInfo.year,
    pageType: carGurusAdInfo.page_type,
    ageType: carGurusAdInfo.age_type,
    cpo: carGurusAdInfo.cpo,
    inLine: carGurusAdInfo.in_line,
    body_style: carGurusAdInfo.body_style,
    category: carGurusAdInfo.category,
    app: carGurusAdInfo.app,
    sellerId: carGurusAdInfo.seller_id,
  });

  useEffectOnce(() => {
    logger.log(
      VdpPageViewEvent({
        trackingParams: viewModel.trackingParams,
        sourceUrl: window?.location?.href,
      })
    );
    trackScreenView({ page: 'vdp', location: `listings/${vehicle.vin}` });
  });

  const { t } = useTranslation();
  const leadSubmitter = useLeadSubmitter(SourcePages.Vdp);
  const { zip } = useAutolistUser();

  const [quickPicksModalIsOpen, setQuickPicksModalIsOpen] = useState(false);
  const [quickPicksState, trackedFetchQuickPicks] = useAsyncFn(async () => {
    return fetchQuickPicks(vehicle.vin) as Promise<SearchResponse>;
  });

  type SourceFeature = Features.modalLeadForm | Features.leadForm | Features.quickPicks;

  async function submitLead(
    { sendAlerts, ...contactInfo }: LeadFormFields,
    sourceFeature: SourceFeature
  ): Promise<void> {
    const sourcePage =
      isMobile || isTablet ? LeadTrackingPageSource.MobileVdp : LeadTrackingPageSource.DesktopVdp;
    const platform =
      isMobile || isTablet
        ? EnhancedLeadTrackingPlatform.Mobile
        : EnhancedLeadTrackingPlatform.Desktop;

    try {
      // if user is not authenticated or has no zip, persist vehicle zip
      if (!contactInfo.zip) {
        // eslint-disable-next-line no-param-reassign
        contactInfo.zip = vehicle.zip;
      }

      const leadResponse = await leadSubmitter({
        vehicle,
        contactInfo,
        emailAcceptance: {
          price: sendAlerts || false,
          search: sendAlerts || false,
          marketing: sendAlerts || false,
        },
        sourcePage,
        sourceFeature,
        geolocation,
        requestedPrice,
        enhancedSourcePage: EnhancedLeadTrackingPageSource.VDP,
        platform,
        pagePosition,
        adExtId,
        adPartnerId,
      });
      // tags
      trackLeadSuccess({
        vehicle,
        contactInfo,
        leadResponse,
        zip,
        geolocation,
        cplValue: vehicle?.cpl_value,
        price: vehicle.price,
      });
      favorite();

      if (vehicle.quick_picks_eligible) {
        setQuickPicksModalIsOpen(true);
        trackedFetchQuickPicks();
      }
    } catch (e) {
      console.error(e);
      showError(t('errors.generic'), { debug: toPlainObject(e) });
      captureException(e);
      throw new Error('Error submitting lead');
    }
  }

  useFetchFavorites();
  useFetchLeads();
  const similarListings = useSimilarListings(viewModel.vin);

  useEffect(() => {
    if (!quickPicksModalIsOpen) return;

    logger.log(VdpQuickPicksViewEvent);
  }, [logger, quickPicksModalIsOpen]);

  return (
    <TrackingDataContext.Provider
      value={{
        source_page: SourcePages.Vdp,
      }}
    >
      <VdpEventContext.Provider
        value={{
          trackingParams: vehicle.tracking_params,
        }}
      >
        <VdpScrollContainer>
          <Header url={router.asPath} includeAds={true} isVdp={true} vdpUrl={viewModel.vdpUrl} />
          <Vdp
            submitLead={submitLead}
            vehicle={viewModel}
            similarListings={similarListings}
            isFavorited={isFavorited}
            addFavorite={favorite}
            removeFavorite={unfavorite}
            adInfo={carGurusAdInfo}
          />
          <Footer />
          <QuickPicksEventContext.Provider
            value={{
              leadTrackingSourceDesktop: LeadTrackingPageSource.DesktopVdpQuickPicks,
              leadTrackingSourceMobile: LeadTrackingPageSource.MobileVdpQuickPicks,
              sourceQP: SourcePages.VdpQuickPicks,
              sourcePage: SourcePages.Vdp,
              sourcePageCategory: SourcePages.Vdp,
            }}
          >
            <QuickPicksModal
              vehicles={quickPicksState.value?.records || []}
              isLoading={quickPicksState.loading}
              close={(): void => {
                setQuickPicksModalIsOpen(false);
                logger.log(closeEvent({ trackingParams: vehicle.tracking_params }));
              }}
              isOpen={quickPicksModalIsOpen}
            />
          </QuickPicksEventContext.Provider>
        </VdpScrollContainer>
      </VdpEventContext.Provider>
    </TrackingDataContext.Provider>
  );
}

export default VdpPage;
